import { formatMacroValue, formatNumber } from "../../lib/format";
import type { ReviewTotals as ReviewTotalsShape } from "./review-utils";

// Meal totals footer card (plan todo 12): sums skip undefined (plateTotals
// semantics), "—" for nutrients no item defines, a "Some nutrition unknown"
// indicator when any item is incomplete, remaining-vs-daily-targets when
// profile goals + today's summary are available, and the "accurate"-style
// data-completeness summary.

export interface ReviewTotalsProps {
  readonly totals: ReviewTotalsShape;
  readonly remainingKcal?: number; // goal minus consumed today, when known
  readonly showCompleteness: boolean; // "accurate" style
  readonly completeItems: number;
  readonly totalItems: number;
  readonly percent: number; // share of items with full macros (reviewCompleteness)
}

function TotalRow({ label, value, unit }: {
  readonly label: string;
  readonly value: number | undefined;
  readonly unit: string;
}) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-sm text-text-muted">{label}</span>
      <span className="text-sm font-semibold text-text">
        {formatMacroValue(value, unit)}
      </span>
    </div>
  );
}

export function ReviewTotals({
  totals,
  remainingKcal,
  showCompleteness,
  completeItems,
  totalItems,
  percent,
}: ReviewTotalsProps) {
  const afterMealKcal =
    remainingKcal !== undefined && totals.calories !== undefined
      ? remainingKcal - totals.calories
      : undefined;
  return (
    <section
      data-testid="review-totals"
      className="rounded-2xl border border-border bg-surface p-4 shadow-sm"
    >
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
          Meal totals
        </h2>
        {showCompleteness && (
          <span
            data-testid="review-completeness-summary"
            className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${
              completeItems === totalItems && totalItems > 0
                ? "bg-green-100 text-green-700"
                : "bg-amber-100 text-amber-700"
            }`}
          >
            {completeItems}/{totalItems} items complete · {percent}%
          </span>
        )}
      </div>
      <p className="mt-1 text-2xl font-bold text-text" data-testid="review-total-kcal">
        {totals.calories === undefined
          ? "Calories unknown"
          : `${formatNumber(Math.round(totals.calories))} kcal`}
      </p>
      <div className="mt-1 divide-y divide-border/60">
        <TotalRow label="Protein" value={totals.proteinG} unit=" g" />
        <TotalRow label="Carbs" value={totals.carbsG} unit=" g" />
        <TotalRow label="Fat" value={totals.fatG} unit=" g" />
        <TotalRow label="Fibre" value={totals.fiberG} unit=" g" />
      </div>
      {totals.hasUnknown && (
        <p data-testid="review-totals-unknown" className="mt-2 text-xs font-medium text-warning">
          Some nutrition is unknown — missing values are never counted as zero.
        </p>
      )}
      {remainingKcal !== undefined && (
        <p data-testid="review-remaining" className="mt-2 text-xs text-text-muted">
          {formatNumber(Math.round(remainingKcal))} kcal left today
          {afterMealKcal !== undefined
            ? ` · ${formatNumber(Math.round(afterMealKcal))} after this meal`
            : ""}
        </p>
      )}
    </section>
  );
}
